import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import styles from "./card-krookie.module.scss";
import logo from "../../../images/logo-80.svg";
import QrCode from "../../qr-code/qr-code";
import { CardKrookies } from "../../../interfaces/krookie";
import compass from "../../../images/compass-north.svg";

interface CardKrookieProps {
  data: CardKrookies;
}

const CardKrookie: FC<CardKrookieProps> = ({ data }) => {
  const { t } = useTranslation();
  const boldFirstWord = (str: string) => {
    if (!str) return null;
    const words = str.split(" ");
    if (words.length === 0) return str;
    return (
      <>
        <strong>{words[0]}</strong> {words.slice(1).join(" ")}
      </>
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.section}>
          <h4>Sultanate of Oman</h4>
          <p>Operations Department</p>
          <span>{boldFirstWord(data.cityEn.displayValue)}</span>
        </div>
        <img src={logo} alt="logo" />
        <div className={styles.section}>
          <h4>سلطنة عمان</h4>
          <p>دائرة العملیات</p>
          <span>{boldFirstWord(data.cityAr)}</span>
        </div>
      </div>
      <div className={styles.title}>
        <span className={styles.serial}>
          {t("card-krookie.serialNo")}: {data.serial}
        </span>
        <span className={styles.revision}>
          {t("card-krookie.revisionNumber")}: {data.revision}
        </span>
      </div>
      <div className={styles.info}>
        <div className={styles.description}>
          <div className={styles.headerDesc}>
            <div className={styles.titleDesc}>
              <span>
                {t("card-krookie.plotNumber")}: {data.plotNumber}
              </span>
              <p>{t("card-krookie.plotDescription")}</p>
            </div>
            {data.qrCodeLink && (
              <QrCode href={data.qrCodeLink} sizeQr={52} qrOnly={true} />
            )}
          </div>
          <div className={styles.details}>
            <div className={styles.column}>
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("card-krookie.streetNumber")}:
                </span>{" "}
                {data.streetNumber}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>{t("card-krookie.area")}:</span>{" "}
                {data.area} m²
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("card-krookie.height")}:
                </span>{" "}
                {data.height}
              </div>
            </div>

            <div className={styles.column}>
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("card-krookie.phaseNumber")}:
                </span>{" "}
                {data.phase.displayValue}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("card-krookie.landUse")}:
                </span>{" "}
                {data.landUse.displayValue}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("card-krookie.buildArea")}:
                </span>{" "}
                {data.buildArea}
              </div>
            </div>
          </div>
          <div className={styles.setBack}>
            <span className={styles.label}>{t("card-krookie.setback")}:</span>
            <div className={styles.setBackValues}>
              <span className={styles.label}>
                {t("card-krookie.back")}:<p>{data.back}</p>
              </span>
              <span className={styles.label}>
                {t("card-krookie.side")}:<p>{data.side}</p>
              </span>
              <span className={styles.label}>
                {t("card-krookie.front")}:<p>{data.front}</p>
              </span>
            </div>
          </div>
          <div className={styles.tableWrapper}>
            <div className={styles.tableTitle}>
              <span>
                {t("card-krookie.plotNumber")} {data.plotNumber} (
                {t("card-krookie.system")} {data.wgs})
              </span>
              <span>
                {t("card-krookie.area")}= {data.area} m²
              </span>
            </div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.headCell}>{t("card-krookie.line")}</th>
                  <th className={styles.headCell}>{t("card-krookie.north")}</th>
                  <th className={styles.headCell}>{t("card-krookie.east")}</th>
                  <th className={styles.headCell}>
                    {t("card-krookie.distance")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.tableData.map((row, i) => (
                  <tr key={i} className={styles.row}>
                    <td className={styles.cell}>{row.line}</td>
                    <td className={styles.cell}>{row.north}</td>
                    <td className={styles.cell}>{row.east}</td>
                    <td className={styles.cell}>{row.distance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.screenshots}>
          <div className={styles.screenshotWrapper}>
            <img
              className={styles.image}
              src={data.screenshots.firstScreenshot.url}
              alt="screenshot"
            />
            <p>
              {t("card-krookie.scale")}:{data.screenshots.firstScreenshot.scale}{" "}
              <img className={styles.compassImg} src={compass} alt="compass" />
            </p>
          </div>
          <div className={styles.screenshotWrapper}>
            <img
              className={styles.image}
              src={data.screenshots.secondScreenshot.url}
              alt="screenshot"
            />
            <p>
              {t("card-krookie.scale")}:
              {data.screenshots.secondScreenshot.scale}{" "}
              <img className={styles.compassImg} src={compass} alt="compass" />
            </p>
          </div>
        </div>
      </div>
      {data.notes.length > 0 &&
        data.notes.some((note) => note.trim() !== "") && (
          <div className={styles.notes}>
            <span className={styles.titleNotes}>
              {t("card-krookie.notes")}:
            </span>
            <ul className={styles.list}>
              {data.notes
                .filter((note) => note.trim() !== "")
                .map((note, index) => (
                  <li key={index}>
                    {index + 1}.{note}
                  </li>
                ))}
            </ul>
          </div>
        )}
      <div className={styles.footer}>
        <div className={styles.documentInfoWrapper}>
          <div className={styles.documentInfo}>
            <div className={styles.infoBlock}>
              <span>
                {t("card-krookie.previousKrookie")}&nbsp;
                <span className={styles.symbolNumber}>№</span>:
              </span>
              <p>{data.previousKrookieCode}</p>
            </div>
            <div className={styles.infoBlock}>
              <span>
                {t("card-krookie.oldPlot")}&nbsp;
                <span className={styles.symbolNumber}>№</span>:
              </span>
              <p>{data.oldPlotNumber}</p>
            </div>
          </div>
          <div className={styles.documentInfo}>
            <div className={styles.infoBlock}>
              <span>{t("card-krookie.drafter")}:</span>
              <p>{data.drafterName}</p>
            </div>
            <div className={styles.infoBlock}>
              <span>{data.userPosition || t("card-krookie.approver")}:</span>
              <p>{data.directorName}</p>
            </div>
          </div>
          <div className={styles.documentInfo}>
            <div className={styles.infoBlock}>
              <span>{t("card-krookie.signature")}:</span>
              <p>
                {t("card-krookie.date")}: {data.signatureDrafter}
              </p>
            </div>
            <div className={styles.infoBlock}>
              <span>{t("card-krookie.signature")}:</span>
              <p>
                {t("card-krookie.date")}: {data.signatureDirector}
              </p>
            </div>
          </div>
          <div className={styles.documentInfo}>
            <p className={styles.stamp}>{t("card-krookie.stamp")}</p>
          </div>
        </div>
      </div>
      <p className={styles.disclaimer}>{t("card-krookie.disclaimer")}</p>
    </div>
  );
};

export default CardKrookie;
